import createDOMPurify from 'dompurify';
import { marked, Renderer } from 'marked';

/**
 * AI-assistant-specific markdown renderer.
 *
 * Each feature in the app that renders user- or model-supplied markdown owns its own renderer
 * module with its own allow-list — see `renderCommentBody` in the comments lib for the analogue.
 * Going through this single entrypoint (instead of ngx-markdown's global provider) keeps the
 * XSS surface explicit per feature: a future AI assistant change can't silently relax the
 * comments allow-list, and vice versa.
 *
 * Allow-list rationale (this surface is for AI assistant chat bubbles):
 *  - Text formatting + tables + lists are allowed because the model frequently emits them.
 *  - `img` is forbidden — the assistant doesn't return images, and any `<img>` survival would
 *    almost certainly be a prompt-injection vector against the user.
 *  - `iframe` / `svg` / `object` / `embed` / scripts are forbidden (default DOMPurify behaviour
 *    plus the explicit list below).
 *  - `class` / `id` / `style` are forbidden so styling can't be hijacked through innerHTML.
 *  - Anchor `href` is restricted to http(s) / mailto via the link hook.
 */

const renderer = new Renderer();
renderer.html = ({ text }) => escapeHtml(text);
renderer.image = ({ text }) => escapeHtml(text);

const MARKED_OPTIONS = {
  async: false as const,
  breaks: true,
  gfm: true,
  renderer,
};

const ALLOWED_TAGS = [
  'a',
  'blockquote',
  'br',
  'code',
  'del',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
];

const ALLOWED_ATTR = ['href', 'rel', 'target', 'title'];

const FORBID_TAGS = [
  'audio',
  'embed',
  'iframe',
  'img',
  'math',
  'object',
  'picture',
  'source',
  'svg',
  'video',
];

let purifier: ReturnType<typeof createDOMPurify> | null = null;
let linkHookRegistered = false;

function getPurifier(): ReturnType<typeof createDOMPurify> {
  if (purifier) {
    return purifier;
  }

  purifier = createDOMPurify(globalThis.window);
  return purifier;
}

function ensureLinkHookRegistered(domPurify: ReturnType<typeof createDOMPurify>): void {
  if (linkHookRegistered) {
    return;
  }

  domPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeName.toLowerCase() !== 'a') {
      return;
    }

    const anchor = node as Element;
    const href = anchor.getAttribute('href');
    if (!href || !isAllowedLinkTarget(href)) {
      anchor.removeAttribute('href');
      anchor.removeAttribute('target');
      anchor.removeAttribute('rel');
      return;
    }

    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });
  linkHookRegistered = true;
}

function isAllowedLinkTarget(href: string): boolean {
  try {
    const baseUrl = globalThis.location?.origin ?? 'http://localhost';
    const url = new URL(href.trim(), baseUrl);
    return url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'mailto:';
  } catch {
    return false;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return character;
    }
  });
}

/**
 * Renders an AI-assistant chat bubble's markdown to safe HTML suitable for `[innerHTML]`. The
 * single entrypoint for AI-assistant markdown — the renderer + sanitize pipeline stays internal
 * so callers can't accidentally bypass one or the other.
 */
export function renderAiAssistantBody(markdown: string): string {
  const html = marked.parse(markdown, MARKED_OPTIONS) as string;
  const domPurify = getPurifier();
  ensureLinkHookRegistered(domPurify);
  return domPurify.sanitize(html, {
    ALLOW_DATA_ATTR: false,
    ALLOWED_ATTR,
    ALLOWED_TAGS,
    FORBID_ATTR: ['class', 'id', 'style', 'src', 'srcset'],
    FORBID_TAGS,
  });
}
